import React, { useEffect, useRef, useState } from "react";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { handleGetItemsWithParam } from "../../shared/controllers/blogController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import LoadingGrid from "../../utils/loadingGrid";

import left from "./assets/chevron-left.svg";
import right from "./assets/chevron-right.svg";

import "swiper/css";

interface SwiperInstance {
    slideNext: () => void;
    slidePrev: () => void;
    activeIndex: number;
}

interface Project {
    title: string;
    excerpt: string;
    link: string;
}

interface GalleryItem {
    image: string;
    title: string;
}

const ProjectComponent: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [gallery, setGallery] = useState<GalleryItem[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(true);
    const swiperRef = useRef<SwiperInstance | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [activeIndex, setActiveIndex] = useState(0);

    const goToNextSlide = () => {
        if (swiperRef.current) swiperRef.current.slideNext();
    };

    const goToPrevSlide = () => {
        if (swiperRef.current) swiperRef.current.slidePrev();
    };

    const fetchProjects = async () => {
        const params = { type: "Video" };
        try {
            const data = await handleGetItemsWithParam<Project[]>(params);
            setProjects(unwrapApiArray<Project>(data));
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGallery = async () => {
        const params = { type: "Project" };
        try {
            const data = await handleGetItemsWithParam<GalleryItem[]>(params);
            setGallery(unwrapApiArray<GalleryItem>(data));
        } catch (error) {
            console.error("Error fetching gallery:", error);
        } finally {
            setLoadingGallery(false);
        }
    };

    useEffect(() => {
        fetchGallery();
        fetchProjects();
    }, []);

    return (
        <>
            <div className=" flex flex-col py-8 md:py-16 mt-8 md:mt-16 w-full px-4 md:px-14">
                <h3>Our Videos</h3>
                <h2 className=" mt-3 font-Manrope">Watch Our Work in Action</h2>

                <div className=" w-full mt-12 ">
                    {loading ? (
                        <LoadingGrid count={3} />
                    ) : projects.length === 0 ? (
                        <div className="flex w-full flex-row items-center justify-center mt-12">
                            <h3 className="text-center font-Manrope text-lg text-black font-semibold">
                                There are no video posts available at this moment
                            </h3>
                        </div>
                    ) : (
                        <div
                            className={`w-full grid grid-cols-1 mt-8 md:mt-16 ${projects.length === 1
                                ? "md:grid-cols-1"
                                : projects.length === 2
                                    ? "md:grid-cols-2"
                                    : "md:grid-cols-3"
                                } gap-6`}
                        >
                            {projects.map((item: Project, index: number) => (
                                <div
                                    key={index}
                                    className=" bg-[#F3F0E9] rounded-[20px] w-full"
                                >
                                    <iframe
                                        width="100%"
                                        height="315"
                                        src={item.link}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        className=" rounded-t-[20px]"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    ></iframe>

                                    <div className=" w-full p-6 md:p-8">
                                        <p className=" font-Manrope font-bold text-2xl text-black">
                                            {item.title}
                                        </p>
                                        <p className=" mt-6 font-Manrope text-base font-normal text-black">
                                            {item.excerpt}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className=" flex flex-col py-8 md:py-16 mt-8 w-full px-4 md:px-14">
                <div className=" w-full flex items-center justify-between">
                    <div className="">
                        <h3>Our Pictures</h3>
                        <h2 className=" mt-3 font-Manrope">Photo Highlights</h2>
                    </div>
                    <div className=" flex items-center space-x-8">
                        <button
                            onClick={goToPrevSlide}
                            className="h-12 w-12 border border-[#317333] flex items-center justify-center rounded-full"
                        >
                            <img src={left} alt="Previous" />
                        </button>
                        <button
                            onClick={goToNextSlide}
                            className="h-12 w-12 border border-[#317333] flex items-center justify-center rounded-full"
                        >
                            <img src={right} alt="Next" />
                        </button>
                    </div>
                </div>
                <div>
                    {loadingGallery ? (
                        <LoadingGrid count={3} />
                    ) : gallery.length === 0 ? (
                        <div className="flex w-full flex-row items-center justify-center mt-12">
                            <h3 className="text-center font-Manrope text-lg text-black font-semibold">
                                There are no image posts available at this moment
                            </h3>
                        </div>
                    ) : (
                        <Swiper
                            slidesPerView={2}
                            spaceBetween={20}
                            pagination={{
                                clickable: true,
                                el: ".custom-pagination",
                            }}
                            onInit={(swiper: SwiperInstance) => {
                                swiperRef.current = swiper;
                                setActiveIndex(swiper.activeIndex);
                            }}
                            onSlideChange={(swiper: SwiperInstance) => setActiveIndex(swiper.activeIndex)}
                            modules={[Pagination]}
                            className="w-full mt-8 md:mt-12"
                        >
                            {gallery.map((item: GalleryItem, index: number) => (
                                <SwiperSlide key={index} className="w-full">
                                    <div
                                        style={{
                                            backgroundImage: `url(${item.image})`,
                                            backgroundPosition: "center",
                                            backgroundSize: "cover",
                                        }}
                                        className="w-full bg-[#5F6D7E] rounded-[12px] h-[400px]"
                                    ></div>
                                    <h4 className="font-Manrope text-base md:text-lg font-normal mt-3 text-[#615f5f]">
                                        {item.title}
                                    </h4>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}
                </div>
                <div className="custom-pagination flex justify-center w-full mt-12 items-center space-x-1"></div>
            </div>
        </>
    );
};

export default ProjectComponent;
